import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect, PromiseState } from 'react-refetch';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';

import PromiseStateContainer from '../../apiAccess/PromiseStateContainer';
import BASE_URL from '../../apiAccess/urls';
import { mediaQuery, sortFcdbFoodId, renderShowsTotal, linkFoodDetail } from '../../util/util';
import Buttons from '../../util/Buttons';

import 'react-bootstrap-table/dist/react-bootstrap-table-all.min.css';
import './BasicFoodSearchResult.css';

const tableDefault = {
  page: 1,
  size: 25,
  sort: 'shortname',
  order: 'asc',
};

/**
 * Concerns of this component:
 *   fetch /food?q={query}[&group=A-Z]
 *   render search result table
**/
class BasicFoodSearchResult extends Component {
  constructor(props) {
    super(props);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
    this.renderOnFulfillment = this.renderOnFulfillment.bind(this);
  }

  handlePageChange(page, size) {
    const newPage = (page === tableDefault.page) ? '' : page;
    const newSize = (size === tableDefault.size) ? '' : size;
    this.props.handlePageChange(newPage, newSize);
  }

  handleSortChange(sortName, sortOrder) {
    const isDefault = sortName === tableDefault.sort && sortOrder === tableDefault.order;
    const newSortName = isDefault ? '' : sortName;
    const newSortOrder = isDefault ? '' : sortOrder;
    this.props.handleSortChange(newSortName, newSortOrder);
  }

  render() {
    if(!this.props.urlQuery && !this.props.selectedGroup) {
      // if query is not specified, don't fetch and use props.allFoods
      return this.renderOnFulfillment([this.props.allFoods]);
    }
    const { foodFetch } = this.props;
    return (
      <PromiseStateContainer
        ps={PromiseState.all([foodFetch])}
        onFulfillment={this.renderOnFulfillment}
      />
    );
  }

  renderOnFulfillment([foods]) {
    // props validation. invalid => default
    const sizePerPage = this.props.size || tableDefault.size;
    const page = (this.props.page && foods.length > (this.props.page - 1) * sizePerPage)
      ? this.props.page : tableDefault.page;
    const sortColumns = ['shortname', 'recordid'];
    const sortName = sortColumns.includes(this.props.sort) ? this.props.sort : tableDefault.sort;
    const sortOrder = this.props.order || tableDefault.order;

    // CSV config
    const csvHeader = [
      {label: 'Food', key: 'shortname'},
      {label: 'Group', key: 'group_name'},
      {label: 'FCDB Food ID', key: 'recordid'},
    ];
    const csvData = foods.slice().sort((a, b) => {
      if (a.shortname < b.shortname) { return -1; }
      if (a.shortname > b.shortname) { return 1; }
      return 0;
    });
    const csvFilenameQuery = this.props.urlQuery || 'All Foods';
    const groupObject = this.props.groups.find(group => group.code === this.props.selectedGroup);
    const csvFilenameGroup = groupObject ? groupObject.display_label : 'All Groups';
    const csvFilename = `Food Search Result - ${csvFilenameQuery} in ${csvFilenameGroup}.csv`;

    const noDataText = (
      <div>
        <h4>No food was found.</h4>
        <p>
          You could try checking your spelling or setting your food group to &apos;All&apos;.<br />
          It is possible this food isn&apos;t in our database yet.
          Please try visiting <a href='https://fdc.nal.usda.gov/'>FoodData Central (USA)</a> or the
          <a href='https://www.foodstandards.gov.au/science/monitoringnutrients/afcd/'>Australian Food Composition Database</a>.
        </p>
      </div>
    );

    const options = {
      noDataText: noDataText,
      paginationShowsTotal: renderShowsTotal,
      paginationSize: mediaQuery(3, 5),
      sizePerPage: sizePerPage,
      sizePerPageList: [10, 25, 50, 100],
      page: page,
      onPageChange: this.handlePageChange,
      sortName: sortName,
      sortOrder: sortOrder,
      onSortChange: this.handleSortChange,
    };

    return (
      <div className="BasicFoodSearchResult">

        <Buttons
          csvHeader={csvHeader}
          csvData={csvData}
          filename={csvFilename}
        />

        <BootstrapTable
          data={foods}
          hover
          condensed
          bordered={false}
          pagination
          options={options}
        >

          <TableHeaderColumn
            dataField='shortname'
            dataFormat={linkFoodDetail}
            dataSort
          >
            FOOD
          </TableHeaderColumn>

          <TableHeaderColumn
            dataField='group_name'
          >
            GROUP
          </TableHeaderColumn>

          <TableHeaderColumn
            isKey
            dataField='recordid'
            dataFormat={linkFoodDetail}
            dataSort
            sortFunc={sortFcdbFoodId}
          >
            FCDB FOOD ID
          </TableHeaderColumn>
        </BootstrapTable>

      </div>
    );
  }
}

BasicFoodSearchResult.propTypes = {
  foodFetch: PropTypes.instanceOf(PromiseState),
  handlePageChange: PropTypes.func,
  handleSortChange: PropTypes.func,
  allFoods: PropTypes.array,
  groups: PropTypes.array,
  urlQuery: PropTypes.string,
  selectedGroup: PropTypes.string,
  page: PropTypes.number,
  size: PropTypes.number,
  sort: PropTypes.string,
  order: PropTypes.string,
};

const fetch = props => {
  if (!props.urlQuery && !props.selectedGroup) { return null; } // don't fetch and use props.allFoods
  let query = props.urlQuery ? `?q=${props.urlQuery}` : '';
  if (props.selectedGroup) {
    query += query === '' ? '?' : '&';
    query += `group=${props.selectedGroup}`;
  }
  return {
    foodFetch: `${BASE_URL}/food${query}`
  };
};

export default connect(fetch)(BasicFoodSearchResult);



// WEBPACK FOOTER //
// ./src/components/search/basicFood/BasicFoodSearchResult.js